import merge from 'lodash/merge';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import {
  COMMUNITY_GENERIC_TYPES,
  COMMUNITY_LOADED_SOURCE,
  COMMUNITY_ROUTE_NAMES,
  COMMUNITY_VISION_PROMPTS,
} from '@/app/community/constants';
import { communityRouteName } from '@/app/community/routes';
import { useVisionAiPermissions } from '@/components/VisionAi/composables/useVisionAiPermissions';
import {
  INTENT_TYPES,
  REQUEST_CONTEXT_BUTTON_IDENTIFIERS,
  THRESHOLD_MINIMUMS,
} from '@/components/VisionAi/constants';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useCommunityDmsStore } from '@/stores/community-dms';
import { useCommunityInboxViewStore } from '@/stores/community-inbox-view';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useCommunityMessagesStore } from '@/stores/community-messages';
import { useIdentityStore } from '@/stores/identity';

const COMMUNITY_INBOX_ROUTES = [
  COMMUNITY_ROUTE_NAMES.INBOX,
  COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU,
  COMMUNITY_ROUTE_NAMES.ARCHIVE,
  COMMUNITY_ROUTE_NAMES.ALL_MESSAGES,
  COMMUNITY_ROUTE_NAMES.CUSTOM_VIEWS,
];

const SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG = {
  label: 'Summarize Community Insights',
  message: 'Summarize Community Insights',
  intentTypeOverride: INTENT_TYPES.UNIFIED,
  buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_INSIGHTS,
  tooltip: 'Use Vision AI to summarize Community Insights',
};

/** When `handleOverviewAccess` has not run yet, `route.name` is the parent `community` only (e.g. primary nav). */
const SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG = {
  label: 'Summarize View',
  message: 'Summarize view for All Messages',
  intentTypeOverride: INTENT_TYPES.UNIFIED,
  buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_MESSAGES,
  tooltip: 'Use Vision AI to summarize the messages in this view',
};

function hasThreadIdInQuery(route) {
  return route?.query?.id != null && String(route.query.id) !== '';
}

export function useCommunityVisionAi() {
  const communityInteractionStore = useCommunityInteractionStore();
  const communityCommentsStore = useCommunityCommentsStore();
  const communityDmsStore = useCommunityDmsStore();
  const messageStore = useCommunityMessagesStore();
  const inboxViewStore = useCommunityInboxViewStore();
  const identityStore = useIdentityStore();
  const route = useRoute();

  const hasValidCommentsThreshold = computed(
    () =>
      communityCommentsStore.commentDetail?.commentCount >= THRESHOLD_MINIMUMS.COMMUNITY.COMMENTS,
  );

  const hasValidDmsThreshold = computed(() => {
    const interaction = communityInteractionStore.selectedCommunityInteraction;
    const conversationId = interaction?.conversation?.id;
    if (conversationId) {
      return (
        (messageStore.messagesByConversationId[conversationId]?.length ?? 0) >=
        THRESHOLD_MINIMUMS.COMMUNITY.DMS
      );
    }
    return (
      communityDmsStore.messages[communityInteractionStore.selectedCommunityInteractionId]
        ?.length >= THRESHOLD_MINIMUMS.COMMUNITY.DMS
    );
  });

  const selectedInteractionType = computed(
    () => communityInteractionStore?.selectedInteractionGenericType,
  );

  const onCommunityRoute = computed(() => route?.name?.startsWith('community'));

  const isCommunityEmptyState = computed(() =>
    Boolean(!selectedInteractionType.value && onCommunityRoute.value),
  );

  const { userHasVisionAiPermissionForCurrentRoute } = useVisionAiPermissions();
  const { canAccessCommunityOverview } = useCommunityPermissions();

  const routeViewId = computed(() =>
    route?.params?.viewId ? parseInt(route.params.viewId, 10) : null,
  );

  const isCustomViewRoute = computed(() => route.meta?.customViewRoute && !!routeViewId.value);

  const pageTitle = computed(() => {
    if (route.meta?.customViewRoute && routeViewId.value) {
      const activeView = inboxViewStore.inboxViewList.find((view) => view.id === routeViewId.value);
      return activeView?.name;
    }
    if (route?.name === communityRouteName) {
      if (canAccessCommunityOverview.value) {
        return SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG;
      }
      return SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG;
    }
    return route.meta?.title;
  });

  // PromptConfig for buttons that should Summarize a Community View
  const summarizeViewConfig = computed(() => ({
    label: 'Summarize View',
    message: `Summarize view for ${pageTitle.value}`,
    intentTypeOverride: INTENT_TYPES.UNIFIED,
    buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_MESSAGES,
    tooltip: 'Use Vision AI to summarize the messages in this view',
  }));

  // Returns the summarize button config that should be shown in the page header
  const summarizeButtonConfig = computed(() => {
    if (
      !userHasVisionAiPermissionForCurrentRoute.value ||
      route?.name === COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES
    )
      return null;

    if (route?.name === communityRouteName) {
      if (hasThreadIdInQuery(route)) {
        return { ...SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG };
      }
      if (canAccessCommunityOverview.value) {
        return SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG;
      }
      return { ...SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG };
    }

    if (route?.name === COMMUNITY_ROUTE_NAMES.INSIGHTS) {
      return SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG;
    }

    if (COMMUNITY_INBOX_ROUTES.includes(route?.name)) {
      return summarizeViewConfig.value;
    }

    return null;
  });

  // Returns a config for the "summarize x" suggested prompt that appears when starting a new chat
  const defaultSummarizePromptConfig = computed(() => {
    if (
      !userHasVisionAiPermissionForCurrentRoute.value ||
      route?.name === COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES
    )
      return null;

    if (route?.name === communityRouteName) {
      if (hasThreadIdInQuery(route)) {
        return { ...SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG };
      }
      if (canAccessCommunityOverview.value) {
        return SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG;
      }
      return { ...SUMMARIZE_ALL_MESSAGES_VIEW_CONFIG };
    }

    if (route?.name === COMMUNITY_ROUTE_NAMES.INSIGHTS) {
      return SUMMARIZE_COMMUNITY_INSIGHTS_CONFIG;
    }

    if (selectedInteractionType.value === COMMUNITY_GENERIC_TYPES.COMMENT) {
      const interaction = communityInteractionStore.selectedCommunityInteraction;
      return {
        message: COMMUNITY_VISION_PROMPTS[COMMUNITY_GENERIC_TYPES.COMMENT],
        intentTypeOverride: INTENT_TYPES.UNIFIED,
        buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMENT_THREAD,
        extra: { mediaId: interaction?.mediaId, brandId: interaction?.brandId },
      };
    }

    if (selectedInteractionType.value === COMMUNITY_GENERIC_TYPES.CONVERSATION) {
      const interaction = communityInteractionStore.selectedCommunityInteraction;
      return {
        message: COMMUNITY_VISION_PROMPTS[COMMUNITY_GENERIC_TYPES.CONVERSATION],
        intentTypeOverride: INTENT_TYPES.UNIFIED,
        buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_DM_THREAD,
        extra: {
          communityInteractionId: interaction?.conversation?.id ?? interaction?.id,
          brandId: interaction?.brandId,
        },
      };
    }

    if (COMMUNITY_INBOX_ROUTES.includes(route?.name)) {
      return summarizeViewConfig.value;
    }

    return null;
  });

  const communityIntentType = computed(() => {
    switch (selectedInteractionType.value) {
      case COMMUNITY_GENERIC_TYPES.COMMENT:
        return INTENT_TYPES.COMMENTS;
      case COMMUNITY_GENERIC_TYPES.CONVERSATION:
        return INTENT_TYPES.DMS;
      default:
        return undefined;
    }
  });

  const communityIntentQuery = computed(() => {
    const interaction = communityInteractionStore.selectedCommunityInteraction;
    const brand = identityStore.allUserBrands.find((b) => b.id === interaction.brandId);

    let query = {
      brandId: brand.id,
      searchParams: {
        limit: 100,
        offset: 0,
      },
      sourceLinkParams: {
        id: interaction.id,
        brandLabel: brand.label,
      },
    };

    if (selectedInteractionType.value === COMMUNITY_GENERIC_TYPES.COMMENT) {
      const platform = interaction.type.split('_')[0];
      query = merge(query, {
        searchParams: {
          mediaId: interaction?.mediaId,
          brandId: brand.id,
        },
        sourceLinkParams: {
          platform,
        },
      });
    }

    if (selectedInteractionType.value === COMMUNITY_GENERIC_TYPES.CONVERSATION) {
      query.searchParams.communityInteractionId = interaction?.conversation?.id ?? interaction.id;
    }

    return query;
  });

  function getCommentUrlFromIntentQuery(intentQuery) {
    const { id, brandLabel } = intentQuery?.sourceLinkParams || {};
    return {
      name: `${COMMUNITY_ROUTE_NAMES.ALL_MESSAGES}`,
      params: {
        brandLabel,
      },
      query: {
        id,
        source: COMMUNITY_LOADED_SOURCE.VISION_AI,
      },
    };
  }

  function getDMUrlFromIntentQuery(intentQuery) {
    const { id, brandLabel } = intentQuery?.sourceLinkParams || {};
    return {
      name: `${COMMUNITY_ROUTE_NAMES.ALL_MESSAGES}`,
      params: {
        brandLabel,
      },
      query: {
        id,
      },
    };
  }

  return {
    hasValidCommentsThreshold,
    hasValidDmsThreshold,
    communityIntentType,
    communityIntentQuery,
    summarizeButtonConfig,
    defaultSummarizePromptConfig,
    isCustomViewRoute,
    onCommunityRoute,
    isCommunityEmptyState,
    pageTitle,
    selectedInteractionType,
    getCommentUrlFromIntentQuery,
    getDMUrlFromIntentQuery,
  };
}
